import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Billing, Service } from 'webapp-types';
import { BillingCycle } from 'webapp/enums/enums';

import BillingAddonComponent from 'webapp/components/billing/addon';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';

interface Args {
	forBillingCycle?: BillingCycle;
	trackingOrigin?: string;
	showHelp?: boolean;
	layout?: 'list' | 'grid';
}

export default class BillingAddonsComponent extends Component<Args> {
	@service('billing') declare billingService: Service.Billing;
	@service('flow/cancellation') declare flowCancellationService: Service.Flow.Cancellation;
	@service('account') declare accountService: Service.Account;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('flow/promotions') declare flowPromotionsService: Service.Flow.Promotions;

	get billingCycle(): BillingCycle {
		return (
			this.args.forBillingCycle ||
			this.billingService.activePlan?.billingCycle ||
			BillingCycle.MONTHLY
		);
	}

	get model(): Billing.Addon[] {
		const billingCycle = this.billingCycle;
		return this.billingService.prepareAddonsList(billingCycle).map((addonInfo) => {
			const addon = Object.assign({}, addonInfo);
			addon.isDisabled = this.billingService.isPaused;
			addon.tooltipText =
				this.flowCancellationService.disabledTooltipTextForAddonsAndServices;
			addon.timeleftText = this.flowPromotionsService.getActiveTrialAddonPromotion(
				addon.id,
			)?.timeLeftText;

			return addon;
		});
	}

	get canRequestSetupHelp(): boolean {
		return !this.currentUserService.isClientUserInPaidClientSpace;
	}

	get isListLayout(): boolean {
		return this.args.layout === 'list';
	}

	get layoutClass(): string {
		return this.isListLayout ? 'addons--list' : 'addons--grid';
	}

	// The list layout only shows active (enabled) add-ons; the grid shows all.
	get visibleAddons(): Billing.Addon[] {
		return this.isListLayout ? this.model.filter((addon) => addon.isEnabled) : this.model;
	}

	// Hide the whole section when there are no add-ons to show.
	get showSection(): boolean {
		return this.visibleAddons.length > 0;
	}

	<template>
		{{#if this.showSection}}
			<div class="addons {{this.layoutClass}}">
				<div class="addons__header">
					<h1>{{if this.isListLayout "Active add-ons" "Add-ons"}}</h1>
					{{#if @showHelp}}
						{{#if this.canRequestSetupHelp}}
							<div class="help">
								<span>Need help?</span>
								<a
									role="button"
									href={{this.accountService.requestSetupHelpLink}}
									target="_blank"
								>
									<CommonUiIconComponent @name="Phone" @size="sm" />
									Book a call
								</a>
							</div>
						{{/if}}
					{{/if}}
				</div>

				<div class="addons__body">
					<ul>
						{{#each this.visibleAddons as |item|}}
							<BillingAddonComponent
								@item={{item}}
								@billingCycle={{this.billingCycle}}
								@trackingOrigin={{@trackingOrigin}}
							/>
						{{/each}}
					</ul>

					{{#if this.isListLayout}}
						<p class="addons__footer">
							Other add-ons are available on the
							<LinkTo @route="settings.billing.plans">Plans page</LinkTo>.
						</p>
					{{/if}}
				</div>
			</div>
		{{/if}}
	</template>
}
